import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { ConsumptionEntryDrawer } from "../components/consumption/ConsumptionEntryDrawer";
import { Icon } from "../components/ui/Icon";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { ToastStack, type ToastItem } from "../components/ui/Toast";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard } from "../components/mobile/primitives";
import "./Items.css";
import "./Consumption.css";

const PAGE_SIZE = 50;

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}
function fmtMoney(n: number): string {
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}
function fmtDateTime(d: string | Date): string {
  return new Intl.DateTimeFormat("tr-TR", { timeZone: "Europe/Istanbul", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(d));
}
function istToday(): string {
  return new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
}
function daysBefore(dateStr: string, n: number): string {
  const base = new Date(`${dateStr}T00:00:00+03:00`).getTime() - n * 86400000;
  return new Date(base + 3 * 3600 * 1000).toISOString().slice(0, 10);
}

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: "8px 14px", fontSize: 13, fontWeight: active ? 600 : 500,
  color: active ? "var(--primary)" : "var(--neutral-600)",
  background: "transparent", border: 0,
  borderBottom: active ? "2px solid var(--primary)" : "2px solid transparent",
  cursor: "pointer", marginBottom: -1, whiteSpace: "nowrap",
});

type Tab = "ozet" | "manuel";

export function ConsumptionPage() {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<Tab>("ozet");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const pushToast = useCallback((t: Omit<ToastItem, "id">) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { ...t, id }]);
  }, []);
  const dismissToast = useCallback((id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);

  // Ortak filtreler
  const [dateTo, setDateTo] = useState(istToday());
  const [dateFrom, setDateFrom] = useState(daysBefore(istToday(), 30));
  const [q, setQ] = useState("");
  const [shiftFilter, setShiftFilter] = useState("");
  const [page, setPage] = useState(1);

  const utils = trpc.useUtils();
  const wsQ = trpc.settings.workSchedule.get.useQuery(undefined, { staleTime: 5 * 60_000 });
  const shifts = (wsQ.data?.shifts ?? []).filter((s) => s.isActive);

  const byItemQ = trpc.consumption.byItem.useQuery(
    { dateFrom, dateTo, q: q.trim() || undefined, shiftId: shiftFilter || undefined, page, pageSize: PAGE_SIZE },
    { enabled: tab === "ozet" },
  );
  const listQ = trpc.consumption.list.useQuery(
    { dateFrom, dateTo, source: "manual", page, pageSize: PAGE_SIZE },
    { enabled: tab === "manuel" },
  );
  const removeMut = trpc.consumption.remove.useMutation();

  const activeQ = tab === "ozet" ? byItemQ : listQ;
  const total = activeQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const switchTab = (t: Tab) => { setTab(t); setPage(1); };
  const resetFilters = () => {
    setDateTo(istToday()); setDateFrom(daysBefore(istToday(), 30));
    setQ(""); setShiftFilter(""); setPage(1);
  };

  async function confirmRemove() {
    if (!confirmId) return;
    try {
      await removeMut.mutateAsync({ id: confirmId });
      await Promise.all([utils.consumption.list.invalidate(), utils.consumption.byItem.invalidate(), utils.inventory.invalidate()]);
      pushToast({ tone: "success", message: "Manuel tüketim kaydı silindi · stok bakiyesi geri yüklendi." });
    } catch (e) {
      pushToast({ tone: "danger", title: "Silinemedi", message: (e as Error).message });
    } finally {
      setConfirmId(null);
    }
  }

  function handleDone(msg: string, warnings: string[]) {
    pushToast({ tone: warnings.length ? "warning" : "success", title: warnings.length ? "Kaydedildi (eksiye düşüldü)" : undefined, message: msg, lines: warnings.length ? warnings : undefined });
    utils.consumption.byItem.invalidate();
    utils.consumption.list.invalidate();
  }

  const byRows = byItemQ.data?.rows ?? [];
  const byTotals = byItemQ.data?.totals;
  const manRows = listQ.data?.rows ?? [];
  const manSummary = listQ.data?.summary ?? { lines: 0, totalCost: 0 };

  const drawers = (
    <>
      <ConsumptionEntryDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onDone={handleDone} />

      <ConfirmDialog
        open={!!confirmId}
        title="Manuel Tüketimi Sil"
        message="Bu manuel tüketim kaydı silinecek ve düşülen stok bakiyesi geri yüklenecek. Devam edilsin mi?"
        confirmLabel="Sil"
        tone="danger"
        busy={removeMut.isPending}
        onConfirm={confirmRemove}
        onCancel={() => setConfirmId(null)}
      />

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </>
  );

  if (isMobile) {
    const pager = totalPages > 1 ? (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
        <button type="button" className="btn-md secondary sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Önceki</button>
        <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page} / {totalPages}</span>
        <button type="button" className="btn-md secondary sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki ›</button>
      </div>
    ) : null;

    const hasRows = tab === "ozet" ? byRows.length > 0 : manRows.length > 0;

    return (
      <>
        <div className="m-stickytop">
          <button type="button" className="btn-md primary" onClick={() => setDrawerOpen(true)}>
            <Icon name="plus" size={15} />Manuel Tüketim
          </button>
          <div className="m-chips">
            {([["ozet", "Özet"], ["manuel", "Manuel Kayıtlar"]] as Array<[Tab, string]>).map(([v, lb]) => (
              <button key={v} type="button" className={`m-chip${tab === v ? " act" : ""}`} onClick={() => switchTab(v)}>{lb}</button>
            ))}
          </div>
          {tab === "ozet" ? (
            <div className="m-search">
              <Icon name="search" size={15} />
              <input type="search" placeholder="Stok kartı kodu / adı…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ürün ara" />
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 8 }}>
            <input className="m-input" style={{ fontFamily: "var(--font-sans)", flex: 1 }} type="date" value={dateFrom} max={dateTo} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} aria-label="Başlangıç tarihi" />
            <input className="m-input" style={{ fontFamily: "var(--font-sans)", flex: 1 }} type="date" value={dateTo} min={dateFrom} max={istToday()} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} aria-label="Bitiş tarihi" />
          </div>
          {tab === "ozet" ? (
            <div className="m-chips">
              <button type="button" className={`m-chip${shiftFilter === "" ? " act" : ""}`} onClick={() => { setShiftFilter(""); setPage(1); }}>Tüm Vardiyalar</button>
              {shifts.map((s) => (
                <button key={s.id} type="button" className={`m-chip${shiftFilter === s.id ? " act" : ""}`} onClick={() => { setShiftFilter(s.id); setPage(1); }}>{s.name}</button>
              ))}
            </div>
          ) : null}
        </div>

        {hasRows ? (
          <div style={{ fontSize: 12, color: "var(--neutral-500)", padding: "0 2px" }}>
            {tab === "ozet"
              ? <><b className="mono">{total}</b> stok kartı · toplam tüketim maliyeti <b className="mono" style={{ color: "var(--neutral-800)" }}>{fmtMoney(byTotals?.totalCost ?? 0)} ₺</b></>
              : <><b className="mono">{manSummary.lines}</b> manuel kayıt · toplam tutar <b className="mono" style={{ color: "var(--neutral-800)" }}>{fmtMoney(manSummary.totalCost)} ₺</b></>}
          </div>
        ) : null}

        {activeQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : activeQ.isError ? (
          <div className="m-empty" style={{ color: "var(--danger)" }}>{activeQ.error?.message}</div>
        ) : tab === "ozet" ? (
          byRows.length === 0 ? (
            <div className="m-empty">Seçili tarih aralığında stok kartı tüketimi yok. "Manuel Tüketim" ile kayıt ekleyebilirsiniz.</div>
          ) : (
            <>
              {byRows.map((r) => (
                <MCard pad key={r.itemId}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{r.itemName}</span>
                      <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{r.itemCode}</span>
                    </span>
                    {r.hasExplosion ? <span className="badge b-info no-dot" title="Manuel yarı mamul/mamul tüketiminin reçete patlatması dahil">reçete dahil</span> : null}
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div className="m-ovl">MİKTAR</div>
                      <div className="mono" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-800)", marginTop: 2 }}>{fmtQty(r.totalQty)} {r.uom}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="m-ovl">ORT. MALİYET</div>
                      <div className="mono" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-500)", marginTop: 2 }}>{r.avgUnitCost != null ? fmtMoney(r.avgUnitCost) : "—"}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="m-ovl">TOPLAM ₺</div>
                      <div className="mono" style={{ fontSize: 12.5, fontWeight: 700, color: "var(--neutral-900)", marginTop: 2 }}>{fmtMoney(r.totalCost)}</div>
                    </div>
                    <div style={{ flex: 0.6 }}>
                      <div className="m-ovl">HRK.</div>
                      <div className="mono" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-500)", marginTop: 2 }}>{r.movements || "—"}</div>
                    </div>
                  </div>
                </MCard>
              ))}
              {pager}
            </>
          )
        ) : manRows.length === 0 ? (
          <div className="m-empty">Seçili tarih aralığında manuel tüketim kaydı yok.</div>
        ) : (
          <>
            {manRows.map((r) => (
              <MCard pad key={r.id}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{r.itemName}</span>
                    <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{r.itemCode}</span>
                  </span>
                  {r.shiftName ? <span className="badge b-info no-dot">{r.shiftName}</span> : null}
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 6, fontSize: 11.5, color: "var(--neutral-600)" }}>
                  <span className="mono">{fmtDateTime(r.ts)}</span>
                  <span>Lot <span className="mono">{r.lotCode}</span></span>
                  <span>{r.locationName}</span>
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div className="m-ovl">MİKTAR</div>
                    <div className="mono" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-800)", marginTop: 2 }}>{fmtQty(r.qty)} {r.uom}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="m-ovl">BİRİM MAL.</div>
                    <div className="mono" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-500)", marginTop: 2 }}>
                      {r.unitCost == null ? "—"
                        : r.costSource === "card"
                          ? <span className="cons-cost-est" title="Stok kartına tanımlı fiyat (lot alış fiyatı yok)">{fmtMoney(r.unitCost)}</span>
                          : <span title="Lot alış fiyatı">{fmtMoney(r.unitCost)}</span>}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="m-ovl">TUTAR ₺</div>
                    <div className="mono" style={{ fontSize: 12.5, fontWeight: 700, color: "var(--neutral-900)", marginTop: 2 }}>{r.cost != null ? fmtMoney(r.cost) : "—"}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                  <button type="button" className="m-iconbtn m-li-act danger" aria-label="Manuel tüketim kaydını sil (bakiye geri yüklenir)" onClick={() => setConfirmId(r.id)}>
                    <Icon name="trash" size={16} />
                  </button>
                </div>
              </MCard>
            ))}
            {pager}
          </>
        )}
        {drawers}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Tüketim Beyanı</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Tüketim Beyanı</h1>
          <p className="sub">Stok kartı bazında toplam tüketim + fiili maliyet · reçeteye bağlı + manuel (yarı mamul/mamul reçetesiyle stok kartına indirgenir)</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="btn-md primary" onClick={() => setDrawerOpen(true)}>
            <Icon name="plus" size={14} />Manuel Tüketim
          </button>
          <button type="button" className="btn-md secondary" onClick={() => activeQ.refetch()} disabled={activeQ.isFetching}>
            <Icon name="refresh" size={14} />{activeQ.isFetching ? "Yükleniyor…" : "Yenile"}
          </button>
        </div>
      </div>

      {/* KPI — seçili aralık */}
      <div className="cons-kpi-grid three">
        <div className="cons-kpi" style={{ "--kpi-color": "var(--info)" } as React.CSSProperties}>
          <div className="lbl">Tüketilen Kalem</div>
          <div className="val">{byTotals ? byTotals.itemCount : "—"}<span className="unit">stok kartı</span></div>
          <div className="sub">seçili tarih aralığında</div>
        </div>
        <div className="cons-kpi" style={{ "--kpi-color": "var(--primary)" } as React.CSSProperties}>
          <div className="lbl">Toplam Tüketim Maliyeti</div>
          <div className="val">{byTotals ? fmtMoney(byTotals.totalCost) : "—"}<span className="unit">₺</span></div>
          <div className="sub">lot alış fiyatı / stok kartı fiyatı</div>
        </div>
        <div className="cons-kpi" style={{ "--kpi-color": "var(--success)" } as React.CSSProperties}>
          <div className="lbl">En Yüksek Maliyetli Kalem</div>
          {byTotals?.topItem ? (
            <>
              <div className="val" style={{ fontSize: 16 }} title={byTotals.topItem.name}>{byTotals.topItem.name}</div>
              <div className="sub"><span className="mono">{byTotals.topItem.code}</span> · {fmtMoney(byTotals.topItem.cost)} ₺</div>
            </>
          ) : <div className="val">—</div>}
        </div>
      </div>

      <div className="dt2-wrap">
        {/* Sekmeler */}
        <div className="cons-tabs">
          <button type="button" style={tabStyle(tab === "ozet")} onClick={() => switchTab("ozet")}>Özet (Stok Kartı Bazında)</button>
          <button type="button" style={tabStyle(tab === "manuel")} onClick={() => switchTab("manuel")}>Manuel Kayıtlar</button>
        </div>

        {/* Filtre çubuğu */}
        <div className="cons-filters">
          <div className="cons-fld date">
            <span className="k">Başlangıç</span>
            <input className="form-input" type="date" value={dateFrom} max={dateTo} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
          </div>
          <div className="cons-fld date">
            <span className="k">Bitiş</span>
            <input className="form-input" type="date" value={dateTo} min={dateFrom} max={istToday()} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
          </div>
          {tab === "ozet" ? (
            <>
              <div className="cons-fld item">
                <span className="k">Ürün Ara</span>
                <input className="form-input" type="search" value={q} placeholder="Stok kartı kodu / adı…" onChange={(e) => { setQ(e.target.value); setPage(1); }} />
              </div>
              <div className="cons-fld sel">
                <span className="k">Vardiya</span>
                <select className="form-select" value={shiftFilter} onChange={(e) => { setShiftFilter(e.target.value); setPage(1); }}>
                  <option value="">Tümü</option>
                  {shifts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </>
          ) : null}
          <button type="button" className="btn-md secondary cons-clear" onClick={resetFilters}><Icon name="x" size={14} />Temizle</button>
        </div>

        <div className="dt2-scroll">
          {activeQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : activeQ.isError ? (
            <div className="empty-state"><p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p><p className="sub">{activeQ.error?.message}</p></div>
          ) : tab === "ozet" ? (
            byRows.length === 0 ? (
              <div className="empty-state">
                <p className="ttl">Tüketim yok</p>
                <p className="sub">Seçili tarih aralığında stok kartı tüketimi yok. "Manuel Tüketim" ile kayıt ekleyebilirsiniz.</p>
              </div>
            ) : (
              <table className="dt2">
                <thead>
                  <tr>
                    <th>Stok Kartı</th>
                    <th className="num" style={{ width: 150 }}>Toplam Miktar</th>
                    <th className="num" style={{ width: 130 }}>Ort. Birim Maliyet</th>
                    <th className="num" style={{ width: 150 }}>Toplam Maliyet</th>
                    <th className="num" style={{ width: 90 }}>Hareket</th>
                  </tr>
                </thead>
                <tbody>
                  {byRows.map((r) => (
                    <tr key={r.itemId}>
                      <td>
                        {r.itemName}
                        <span className="sec mono" style={{ fontSize: 10.5 }}>{r.itemCode}</span>
                        {r.hasExplosion ? <span className="cons-explode-tag" title="Manuel yarı mamul/mamul tüketiminin reçete patlatması dahil">reçete dahil</span> : null}
                      </td>
                      <td className="cell-num">{fmtQty(r.totalQty)}<span className="unit">{r.uom}</span></td>
                      <td className="cell-num" style={{ color: "var(--neutral-500)" }}>{r.avgUnitCost != null ? fmtMoney(r.avgUnitCost) : "—"}</td>
                      <td className="cell-num" style={{ fontWeight: 600 }}>{fmtMoney(r.totalCost)}<span className="unit">₺</span></td>
                      <td className="cell-num" style={{ color: "var(--neutral-500)" }}>{r.movements || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : manRows.length === 0 ? (
            <div className="empty-state">
              <p className="ttl">Manuel kayıt yok</p>
              <p className="sub">Seçili tarih aralığında manuel tüketim kaydı yok.</p>
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Tarih · Saat</th>
                  <th style={{ width: 90 }}>Vardiya</th>
                  <th>Ürün</th>
                  <th style={{ width: 120 }}>Lot</th>
                  <th style={{ width: 130 }}>Lokasyon</th>
                  <th className="num" style={{ width: 110 }}>Miktar</th>
                  <th className="num" style={{ width: 110 }}>Birim Maliyet</th>
                  <th className="num" style={{ width: 120 }}>Tutar</th>
                  <th style={{ width: 56 }}></th>
                </tr>
              </thead>
              <tbody>
                {manRows.map((r) => (
                  <tr key={r.id}>
                    <td className="mono" style={{ fontSize: 12 }}>{fmtDateTime(r.ts)}</td>
                    <td>{r.shiftName ? <span className="badge-pill">{r.shiftName}</span> : <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                    <td>
                      {r.itemName}
                      <span className="sec mono" style={{ fontSize: 10.5 }}>{r.itemCode}</span>
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>{r.lotCode}</td>
                    <td style={{ fontSize: 12 }}>{r.locationName}</td>
                    <td className="cell-num">{fmtQty(r.qty)}<span className="unit">{r.uom}</span></td>
                    <td className="cell-num" style={{ color: "var(--neutral-500)" }}>
                      {r.unitCost == null ? "—"
                        : r.costSource === "card"
                          ? <span className="cons-cost-est" title="Stok kartına tanımlı fiyat (lot alış fiyatı yok)">{fmtMoney(r.unitCost)}</span>
                          : <span title="Lot alış fiyatı">{fmtMoney(r.unitCost)}</span>}
                    </td>
                    <td className="cell-num">{r.cost != null ? <>{fmtMoney(r.cost)}<span className="unit">₺</span></> : "—"}</td>
                    <td>
                      <div className="row-acts">
                        <button type="button" className="iact danger" aria-label="Manuel kaydı sil" title="Manuel tüketim kaydını sil (bakiye geri yüklenir)" onClick={() => setConfirmId(r.id)}>
                          <Icon name="trash" size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="dt2-foot">
          {tab === "ozet" ? (
            <div><b>{total}</b> stok kartı · toplam tüketim maliyeti <b className="mono">{fmtMoney(byTotals?.totalCost ?? 0)} ₺</b></div>
          ) : (
            <div><b>{manSummary.lines}</b> manuel kayıt · toplam tutar <b className="mono">{fmtMoney(manSummary.totalCost)} ₺</b></div>
          )}
          <div className="pager">
            <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span className="pg active">{page}</span>
            <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
            <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          </div>
        </div>
      </div>

      {drawers}
    </div>
  );
}
